import React, { useEffect, useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import { Alert, DatePicker, Input, Select } from "antd"
import { useMutation } from "@tanstack/react-query"
import { Button, Label } from "components"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { useAppNotificationStore } from "stores"
import { onQueryError } from "utils/utils"
import { leaveService } from "services/leaveService"

const API = "DD/MM/YYYY"

interface OpenPlanningCycleModalProps {
  open: boolean
  /** Years that already have a cycle. One cycle per year, so they are not offered. */
  takenYears: number[]
  onClose: () => void
  onOpened: () => void
}

/**
 * AL-01. HR opens planning for a year: employees can then enter the periods they intend to
 * take, and supervisors coordinate them on the roster.
 *
 * The opening date can be later than today, so a cycle can be set up ahead and start
 * accepting plans on its own. The deadline is when entries close; reminders go out a week
 * and a day before it.
 */
const OpenPlanningCycleModal: React.FC<OpenPlanningCycleModalProps> = ({
  open,
  takenYears,
  onClose,
  onOpened,
}) => {
  const { toast } = useAppNotificationStore()

  // Planning is done ahead, so next year is the usual choice; this year stays available for
  // a company starting part way through.
  const thisYear = dayjs().year()
  const yearOptions = [thisYear, thisYear + 1, thisYear + 2]
    .filter((year) => !takenYears.includes(year))
    .map((year) => ({ label: String(year), value: year }))

  const [year, setYear] = useState<number | undefined>()
  const [opensOn, setOpensOn] = useState<Dayjs | null>(dayjs())
  const [deadline, setDeadline] = useState<Dayjs | null>(null)
  const [note, setNote] = useState("")

  useEffect(() => {
    if (!open) return
    setYear(
      yearOptions.find((o) => o.value === thisYear + 1)?.value ??
        yearOptions[0]?.value
    )
    setOpensOn(dayjs())
    setDeadline(null)
    setNote("")
    // Reset each time the modal opens, and only then: re-running whenever the derived year
    // options change would wipe what HR has typed.
  }, [open])

  const deadlineBeforeOpening =
    !!deadline && !!opensOn && deadline.isBefore(opensOn, "day")

  const save = useMutation({
    mutationFn: () =>
      leaveService.openPlanningCycle({
        planning_year: String(year),
        opens_on: opensOn?.format(API),
        submission_deadline: deadline?.format(API),
        note: note.trim() || undefined,
      }),
    onSuccess: (res: any) => {
      toast.success(res?.message || "Leave planning is open.")
      onOpened()
    },
    onError: onQueryError,
  })

  const complete = !!year && !deadlineBeforeOpening

  return (
    <CustomModal
      open={open}
      title="Open leave planning"
      onCancel={onClose}
      footer={
        <div className="w-full flex justify-end gap-3">
          <Button
            color="neutral"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={!complete || save.isLoading}
            submitting={save.isLoading}
            onClick={() => complete && save.mutate()}
          >
            Open planning
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label>Year being planned</Label>
          <Select
            value={year}
            onChange={setYear}
            placeholder="Choose a year"
            options={yearOptions}
            notFoundContent="Every nearby year already has a planning cycle."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <Label>Opens on</Label>
            <DatePicker
              value={opensOn}
              onChange={setOpensOn}
              format={API}
              allowClear={false}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label>Entries close (optional)</Label>
            <DatePicker
              value={deadline}
              onChange={setDeadline}
              format={API}
              disabledDate={(d) => !!opensOn && d.isBefore(opensOn, "day")}
            />
          </div>
        </div>
        {deadlineBeforeOpening ? (
          <span className="font-avenir text-xs text-padeRed">
            Entries cannot close before planning opens.
          </span>
        ) : null}

        <div className="flex flex-col gap-2">
          <Label>Note to employees (optional)</Label>
          <Input.TextArea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="For example, what the plan will be used for, or how far ahead to plan."
            maxLength={500}
            showCount
          />
        </div>

        <Alert
          type="info"
          showIcon
          message="What opening planning does"
          description="From the opening date, employees can add the leave they intend to take that year under Requests → Plan. Plans move no balance and need no approval; supervisors coordinate them here, and each plan becomes a real leave request only when the employee converts it."
        />
      </div>
    </CustomModal>
  )
}

export default OpenPlanningCycleModal
